import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faShieldAlt, faVideo, faUserMd } from '@fortawesome/free-solid-svg-icons';
import { signInWithEmail } from '../js/firebase/config';
import holorayLogo from '../assets/images/png/logo_holoray_black.png';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            await signInWithEmail(email, password);
            navigate('/');
        } catch (error: any) {
            let errorMessage = 'An error occurred during authentication';
            switch (error.code) {
                case 'auth/invalid-credential':
                    errorMessage = 'Invalid email or password'; break;
                case 'auth/invalid-email':
                    errorMessage = 'Please enter a valid email address'; break;
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email'; break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password'; break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many failed attempts. Please try again later'; break;
                case 'auth/user-disabled':
                    errorMessage = 'This account has been disabled'; break;
                default:
                    errorMessage = error.message || errorMessage;
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-root">
            {/* Left panel - branding */}
            <div className="login-brand-panel">
                <div className="login-brand-content">
                    <img src={holorayLogo} alt="HoloRay" className="login-brand-logo" />
                    <h2 className="login-brand-title">Professional Medical Collaboration</h2>
                    <p className="login-brand-desc">
                        Secure, real-time video consultations with live annotation tools designed for medical professionals.
                    </p>
                    <div className="login-brand-features">
                        <div className="login-brand-feature">
                            <div className="lbf-icon">
                                <FontAwesomeIcon icon={faShieldAlt} />
                            </div>
                            <div>
                                <span className="lbf-title">Enterprise Security</span>
                                <span className="lbf-desc">End-to-end encrypted sessions</span>
                            </div>
                        </div>
                        <div className="login-brand-feature">
                            <div className="lbf-icon">
                                <FontAwesomeIcon icon={faVideo} />
                            </div>
                            <div>
                                <span className="lbf-title">HD Video Quality</span>
                                <span className="lbf-desc">Crystal-clear medical imaging</span>
                            </div>
                        </div>
                        <div className="login-brand-feature">
                            <div className="lbf-icon">
                                <FontAwesomeIcon icon={faUserMd} />
                            </div>
                            <div>
                                <span className="lbf-title">Live Annotation</span>
                                <span className="lbf-desc">Draw directly on live video feeds</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right panel - form */}
            <div className="login-form-panel">
                <div className="login-form-wrap">
                    <div className="login-form-header">
                        <h1 className="login-form-title">Welcome back</h1>
                        <p className="login-form-sub">Sign in to your HoloRay account</p>
                    </div>

                    {error && (
                        <div className="login-error">
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="lf-field">
                            <label className="lf-label">Email address</label>
                            <div className="lf-input-wrap">
                                <FontAwesomeIcon icon={faEnvelope} className="lf-icon" />
                                <input
                                    className="lf-input"
                                    type="email"
                                    placeholder="doctor@hospital.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div className="lf-field">
                            <label className="lf-label">Password</label>
                            <div className="lf-input-wrap">
                                <FontAwesomeIcon icon={faLock} className="lf-icon" />
                                <input
                                    className="lf-input"
                                    type="password"
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="lf-submit-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="lf-loading-dots">
                                    <span></span><span></span><span></span>
                                </span>
                            ) : 'Sign In'}
                        </button>
                    </form>

                    <div className="login-contact">
                        <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.4rem', opacity: 0.5 }} />
                        Need access? Contact{' '}
                        <a href="mailto:support@holoray.com" className="login-link">support@holoray.com</a>
                    </div>

                    <div className="login-terms">
                        By signing in, you agree to our{' '}
                        <a href="#" className="login-link">Terms of Service</a> and{' '}
                        <a href="#" className="login-link">Privacy Policy</a>
                    </div>
                </div>
            </div>

            <style>{`
                .login-root {
                    height: 100vh;
                    height: 100dvh;
                    max-height: 100dvh;
                    overflow: hidden;
                    display: flex;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .login-brand-panel {
                    flex: 1;
                    min-height: 0;
                    background: linear-gradient(135deg, #0f1724 0%, #1e3a5f 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: clamp(1.5rem, 4vh, 3rem);
                    overflow: hidden;
                }

                @media (max-width: 768px) {
                    .login-brand-panel {
                        display: none;
                    }
                }

                .login-brand-content {
                    max-width: 480px;
                }

                .login-brand-logo {
                    height: 48px;
                    width: auto;
                    filter: brightness(0) invert(1);
                    opacity: 0.9;
                    margin-bottom: 2.5rem;
                    display: block;
                }

                .login-brand-title {
                    font-size: 2rem;
                    font-weight: 800;
                    color: #f8fafc;
                    line-height: 1.2;
                    margin-bottom: 1rem;
                    letter-spacing: -0.02em;
                }

                .login-brand-desc {
                    font-size: 1rem;
                    color: #94a3b8;
                    line-height: 1.65;
                    margin-bottom: 2.5rem;
                }

                .login-brand-features {
                    display: flex;
                    flex-direction: column;
                    gap: 1.25rem;
                }

                .login-brand-feature {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .lbf-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: rgba(37, 99, 235, 0.2);
                    border: 1px solid rgba(37, 99, 235, 0.3);
                    color: #60a5fa;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1rem;
                    flex-shrink: 0;
                }

                .lbf-title {
                    display: block;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #e2e8f0;
                    margin-bottom: 0.1rem;
                }

                .lbf-desc {
                    display: block;
                    font-size: 0.8rem;
                    color: #64748b;
                }

                .login-form-panel {
                    width: min(480px, 100%);
                    flex-shrink: 0;
                    min-height: 0;
                    background: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: clamp(1.25rem, 3vh, 2.5rem) clamp(1.25rem, 3vw, 2.5rem);
                    overflow: hidden;
                }

                @media (max-width: 768px) {
                    .login-form-panel {
                        width: 100%;
                        background: linear-gradient(135deg, #0f1724 0%, #1e3a5f 100%);
                        padding: 1.25rem 1.25rem max(1.25rem, env(safe-area-inset-bottom, 0px));
                        padding-top: max(1.25rem, env(safe-area-inset-top, 0px));
                    }
                }

                .login-form-wrap {
                    width: 100%;
                    max-width: 360px;
                }

                .login-form-header {
                    margin-bottom: 2rem;
                }

                .login-form-title {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: #0f172a;
                    margin-bottom: 0.375rem;
                    letter-spacing: -0.02em;
                }

                .login-form-sub {
                    font-size: 0.9rem;
                    color: #64748b;
                }

                @media (max-width: 768px) {
                    .login-form-title {
                        color: #f8fafc;
                    }

                    .login-form-sub {
                        color: #94a3b8;
                    }
                }

                .login-error {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 10px;
                    padding: 0.75rem 1rem;
                    margin-bottom: 1.25rem;
                    font-size: 0.875rem;
                    color: #dc2626;
                }

                .login-form {
                    margin-bottom: 1.5rem;
                }

                .lf-field {
                    margin-bottom: 1.125rem;
                }

                .lf-label {
                    display: block;
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                @media (max-width: 768px) {
                    .lf-label { color: #94a3b8; }
                }

                .lf-input-wrap {
                    display: flex;
                    align-items: center;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0 0.875rem;
                    background: #f8fafc;
                    transition: border-color 0.15s, background 0.15s;
                }

                .lf-input-wrap:focus-within {
                    border-color: #2563eb;
                    background: #ffffff;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
                }

                @media (max-width: 768px) {
                    .lf-input-wrap {
                        background: rgba(255, 255, 255, 0.06);
                        border-color: rgba(255, 255, 255, 0.12);
                    }

                    .lf-input-wrap:focus-within {
                        background: rgba(255, 255, 255, 0.1);
                        border-color: rgba(37, 99, 235, 0.7);
                    }

                    .lf-input {
                        color: #f1f5f9 !important;
                    }

                    .lf-input::placeholder {
                        color: #475569 !important;
                    }
                }

                .lf-icon {
                    color: #94a3b8;
                    font-size: 0.85rem;
                    margin-right: 0.625rem;
                    flex-shrink: 0;
                }

                .lf-input {
                    flex: 1;
                    background: transparent;
                    border: none;
                    outline: none;
                    padding: 0.75rem 0;
                    font-size: 0.925rem;
                    color: #0f172a;
                    font-family: inherit;
                }

                .lf-input::placeholder {
                    color: #94a3b8;
                }

                .lf-submit-btn {
                    width: 100%;
                    padding: 0.875rem;
                    background: #2563eb;
                    border: none;
                    border-radius: 10px;
                    color: white;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.15s;
                    margin-top: 0.5rem;
                    font-family: inherit;
                    letter-spacing: 0.01em;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 48px;
                }

                .lf-submit-btn:hover:not(:disabled) {
                    background: #1d4ed8;
                    transform: translateY(-1px);
                    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3);
                }

                .lf-submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .lf-loading-dots {
                    display: flex;
                    gap: 4px;
                    align-items: center;
                }

                .lf-loading-dots span {
                    width: 6px;
                    height: 6px;
                    background: white;
                    border-radius: 50%;
                    animation: lf-dot-bounce 1.2s infinite;
                    opacity: 0.8;
                }

                .lf-loading-dots span:nth-child(2) { animation-delay: 0.2s; }
                .lf-loading-dots span:nth-child(3) { animation-delay: 0.4s; }

                @keyframes lf-dot-bounce {
                    0%, 80%, 100% { transform: scale(0.8); opacity: 0.4; }
                    40% { transform: scale(1.1); opacity: 1; }
                }

                .login-contact {
                    font-size: 0.8rem;
                    color: #64748b;
                    text-align: center;
                    margin-bottom: 0.875rem;
                    padding: 0.75rem;
                    background: #f8fafc;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }

                .login-terms {
                    font-size: 0.75rem;
                    color: #94a3b8;
                    text-align: center;
                    line-height: 1.5;
                }

                @media (max-width: 768px) {
                    .login-contact {
                        background: rgba(255, 255, 255, 0.04);
                        border-color: rgba(255, 255, 255, 0.08);
                        color: #64748b;
                    }
                }

                .login-link {
                    color: #2563eb;
                    text-decoration: none;
                    font-weight: 500;
                }

                .login-link:hover {
                    text-decoration: underline;
                }
            `}</style>
        </div>
    );
}
